import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Users,
  IdCard,
  Network,
  Briefcase,
  ListChecks,
  Plane,
  Table2,
  Download,
  Pencil,
  PartyPopper,
} from 'lucide-react';
import { apiReports } from '../api/client.js';
import { useFilterStore, toFilterQuery } from '../hooks/useFilterStore.js';
import ReportControls from '../components/ReportControls.jsx';
import DashboardWidget from '../components/DashboardWidget.jsx';
import LineAreaChart from '../components/charts/LineAreaChart.jsx';
import { exportRowsToXlsx } from '../components/reports/exportXlsx.js';
import { exportRowsToCsv } from '../components/reports/exportCsv.js';
import LoggedTimeEditModal from '../components/reports/LoggedTimeEditModal.jsx';
import { convertReportHours } from '../lib/reportUnits.js';
import { timetrackingExportColumns } from '../lib/timetrackingExport.js';

// ---------- Onglets de breakdown (Float-like) ----------
const BREAKDOWN_TABS = [
  { key: 'people', label: 'People', Icon: Users },
  { key: 'roles', label: 'Roles', Icon: IdCard },
  { key: 'departments', label: 'Departments', Icon: Network },
  { key: 'projects', label: 'Projects', Icon: Briefcase },
  { key: 'tasks', label: 'Tasks', Icon: ListChecks },
  { key: 'timeoff', label: 'Time off', Icon: Plane },
  { key: 'timetracking', label: 'Time tracking', Icon: Table2 },
];

const KPI_CARDS = [
  { key: 'capacityHours', label: 'Capacité', color: 'var(--text-muted)' },
  { key: 'loggedHours', label: 'Saisi', color: 'var(--accent)' },
  { key: 'billableHours', label: 'Facturable', color: 'var(--success)' },
  { key: 'nonBillableHours', label: 'Non facturable', color: 'var(--text-muted)' },
  { key: 'unloggedHours', label: 'Non imputé', color: 'var(--warning)' },
  { key: 'timeoffHours', label: 'Congés', color: 'var(--text-muted)' },
  { key: 'overtimeHours', label: 'Heures sup.', color: 'var(--danger)' },
];

/**
 * Vue Rapports & Analytics : 1 grand chart capacité vs demande + table
 * détaillée. Le scope est borné par le rôle côté serveur.
 *
 * `reportContext` (drill-down depuis Accueil) pré-remplit période/viewMode/scope.
 */

// ---------- Helpers période (UTC, cohérent backend) ----------

function startOfUtcDay(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function addDays(d, n) {
  const r = new Date(d);
  r.setUTCDate(r.getUTCDate() + n);
  return r;
}
function isoOf(d) {
  return d.toISOString().slice(0, 10);
}
function mondayOf(d) {
  const wd = ((d.getUTCDay() + 6) % 7) + 1;
  return addDays(startOfUtcDay(d), -(wd - 1));
}

/** Fenêtre par défaut + pas de navigation selon la granularité. */
const WINDOW_BY_PERIOD = {
  day: { spanDays: 21, shiftDays: 7 },
  week: { spanDays: 84, shiftDays: 28 },
  month: { spanDays: 180, shiftDays: 60 },
  quarter: { spanDays: 365, shiftDays: 90 },
};

function defaultWindow(period, anchor = new Date()) {
  const cfg = WINDOW_BY_PERIOD[period] ?? WINDOW_BY_PERIOD.week;
  const from = mondayOf(anchor);
  const to = addDays(from, cfg.spanDays - 1);
  return { from: isoOf(from), to: isoOf(to) };
}

// ---------- Presets de fenêtre temporelle (façon Float, UTC) ----------
// Chaque preset retourne { from, to, period } (granularité adaptée).
function monthStart(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function monthEnd(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
}
function quarterStart(d) {
  const q = Math.floor(d.getUTCMonth() / 3);
  return new Date(Date.UTC(d.getUTCFullYear(), q * 3, 1));
}
function quarterEnd(d) {
  const q = Math.floor(d.getUTCMonth() / 3);
  return new Date(Date.UTC(d.getUTCFullYear(), q * 3 + 3, 0));
}
const W = (from, to, period) => ({ from: isoOf(from), to: isoOf(to), period });

const PERIOD_PRESETS = [
  { key: 'next12weeks', label: '12 prochaines semaines', compute: (n) => W(mondayOf(n), addDays(mondayOf(n), 12 * 7 - 1), 'week') },
  { key: 'thisWeek', label: 'Cette semaine', compute: (n) => W(mondayOf(n), addDays(mondayOf(n), 6), 'day') },
  { key: 'thisMonth', label: 'Ce mois', compute: (n) => W(monthStart(n), monthEnd(n), 'day') },
  { key: 'thisQuarter', label: 'Ce trimestre', compute: (n) => W(quarterStart(n), quarterEnd(n), 'week') },
  { key: 'thisYear', label: 'Cette année', compute: (n) => W(new Date(Date.UTC(n.getUTCFullYear(), 0, 1)), new Date(Date.UTC(n.getUTCFullYear(), 11, 31)), 'month') },
  { key: 'lastWeek', label: 'Semaine dernière', compute: (n) => W(addDays(mondayOf(n), -7), addDays(mondayOf(n), -1), 'day') },
  { key: 'lastMonth', label: 'Mois dernier', compute: (n) => { const p = monthStart(addDays(monthStart(n), -1)); return W(p, monthEnd(p), 'day'); } },
  { key: 'lastQuarter', label: 'Trimestre dernier', compute: (n) => { const p = quarterStart(addDays(quarterStart(n), -1)); return W(p, quarterEnd(p), 'week'); } },
  { key: 'lastYear', label: 'Année dernière', compute: (n) => { const y = n.getUTCFullYear() - 1; return W(new Date(Date.UTC(y, 0, 1)), new Date(Date.UTC(y, 11, 31)), 'month'); } },
];

// ---------- Ticket #97 — paramètres conservés au rafraîchissement ----------
// Période, granularité, affichage, périmètre (et onglet) survivent à un F5. En
// `sessionStorage` : propre à l'onglet du navigateur, sans polluer l'URL que
// partagent toutes les vues. Un preset nommé (« Ce mois »…) est recalculé à la
// restauration pour rester relatif à aujourd'hui.
const REPORT_STATE_KEY = 'synolog_reports_state';
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function readReportState() {
  try {
    const v = JSON.parse(globalThis.sessionStorage?.getItem(REPORT_STATE_KEY) ?? 'null');
    return v && typeof v === 'object' ? v : null;
  } catch {
    return null;
  }
}

function writeReportState(state) {
  try {
    globalThis.sessionStorage?.setItem(REPORT_STATE_KEY, JSON.stringify(state));
  } catch {
    /* stockage indisponible (navigation privée…) : sans persistance */
  }
}

/** Fenêtre + granularité + preset restaurés, ou null si rien d'exploitable. */
function restoredWindow(saved) {
  if (!saved) return null;
  const preset = PERIOD_PRESETS.find((p) => p.key === saved.preset);
  if (preset) {
    const w = preset.compute(new Date());
    return { preset: preset.key, period: w.period, window: { from: w.from, to: w.to } };
  }
  const w = saved.window;
  if (!WINDOW_BY_PERIOD[saved.period] || !ISO_DAY.test(w?.from ?? '') || !ISO_DAY.test(w?.to ?? '')) {
    return null;
  }
  return { preset: 'custom', period: saved.period, window: { from: w.from, to: w.to } };
}

function fmtBucket(iso, period) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (period === 'day') return `${d}/${m}`;
  if (period === 'week') return `${d}/${m}`;
  if (period === 'month') return `${m}/${y.slice(2)}`;
  if (period === 'quarter') {
    const q = Math.floor((Number(m) - 1) / 3) + 1;
    return `T${q} ${y.slice(2)}`;
  }
  return iso;
}

const th = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 11,
  color: 'var(--text-muted)',
  borderBottom: '1px solid var(--glass-border)',
  fontWeight: 600,
};
const td = {
  padding: '7px 10px',
  fontSize: 12,
  color: 'var(--text)',
  borderBottom: '1px solid var(--glass-border)',
};

export default function ReportsView({ user, reportContext }) {
  const isAdmin = user?.isAdmin || user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER';
  // Ticket #45 — « Tout le monde » est proposé à tous les rôles (lecture
  // seule) ; « Mon équipe » reste réservé à ceux qui en ont une.
  const availableScopes = isManager || isAdmin
    ? ['self', 'team', 'all']
    : ['self', 'all'];
  // Le périmètre par défaut ne change PAS avec l'ouverture : un utilisateur
  // simple atterrit toujours sur ses propres chiffres, un manager sur son
  // équipe, un admin sur l'ensemble. « Tout le monde » se choisit.
  const defaultScope = isAdmin ? 'all' : isManager ? 'team' : 'self';

  // Ticket #97 — état sauvegardé, ignoré quand on arrive par un drill-down
  // (le contexte de l'Accueil prime).
  const [saved] = useState(() => (reportContext ? null : readReportState()));
  const savedWin = restoredWindow(saved);

  const [unit, setUnit] = useState(saved?.unit === 'days' ? 'days' : 'hours'); // affichage uniquement
  // Défaut : week-ends ignorés.
  const [ignoreWeekends, setIgnoreWeekends] = useState(
    typeof saved?.ignoreWeekends === 'boolean' ? saved.ignoreWeekends : true,
  );
  const [viewMode, setViewMode] = useState(
    reportContext?.viewMode ?? (['scheduled', 'logged'].includes(saved?.viewMode) ? saved.viewMode : 'scheduled'),
  );
  const [period, setPeriod] = useState(reportContext?.period ?? savedWin?.period ?? 'week');
  const [scope, setScope] = useState(
    availableScopes.includes(reportContext?.scope)
      ? reportContext.scope
      : availableScopes.includes(saved?.scope)
      ? saved.scope
      : defaultScope,
  );
  // Profondeur du périmètre "team" : 'direct' (managés directs) | 'all' (descendance).
  const [teamDepth, setTeamDepth] = useState(saved?.teamDepth === 'direct' ? 'direct' : 'all');
  const [window, setWindow] = useState(
    reportContext?.from && reportContext?.to
      ? { from: reportContext.from, to: reportContext.to }
      : savedWin?.window ?? defaultWindow(reportContext?.period ?? 'week'),
  );
  // Preset de fenêtre actif ('custom' = période ajustée manuellement).
  const [preset, setPreset] = useState(
    reportContext?.from ? 'custom' : savedWin?.preset ?? 'next12weeks',
  );

  const { conditions } = useFilterStore('schedule');
  const filterQuery = useMemo(() => toFilterQuery(conditions), [conditions]);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Re-applique le contexte de drill-down quand il change.
  useEffect(() => {
    if (!reportContext) return;
    if (reportContext.viewMode) setViewMode(reportContext.viewMode);
    if (reportContext.period) setPeriod(reportContext.period);
    if (reportContext.scope && availableScopes.includes(reportContext.scope)) {
      setScope(reportContext.scope);
    }
    if (reportContext.from && reportContext.to) {
      setWindow({ from: reportContext.from, to: reportContext.to });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportContext]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiReports
      .utilization({ from: window.from, to: window.to, period, viewMode, scope, teamDepth, filter: filterQuery, includeWeekends: !ignoreWeekends })
      .then((r) => setReport(r))
      .catch((e) => setError(e?.message || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [window.from, window.to, period, viewMode, scope, teamDepth, filterQuery, ignoreWeekends]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- Onglet de breakdown ----
  const [tab, setTab] = useState(
    BREAKDOWN_TABS.some((t) => t.key === saved?.tab) ? saved.tab : 'people',
  );

  useEffect(() => {
    writeReportState({ unit, ignoreWeekends, viewMode, period, scope, teamDepth, window, preset, tab });
  }, [unit, ignoreWeekends, viewMode, period, scope, teamDepth, window, preset, tab]);
  const [breakdown, setBreakdown] = useState(null);
  const [bdLoading, setBdLoading] = useState(false);
  const [bdError, setBdError] = useState(null);

  const loadBreakdown = useCallback(() => {
    setBdLoading(true);
    setBdError(null);
    apiReports
      .breakdown({ dimension: tab, from: window.from, to: window.to, period, viewMode, scope, teamDepth, filter: filterQuery, includeWeekends: !ignoreWeekends })
      .then((r) => setBreakdown(r))
      .catch((e) => setBdError(e?.message || 'Erreur de chargement'))
      .finally(() => setBdLoading(false));
  }, [tab, window.from, window.to, period, viewMode, scope, teamDepth, filterQuery, ignoreWeekends]);

  useEffect(() => {
    loadBreakdown();
  }, [loadBreakdown]);

  // Application d'un preset de fenêtre (façon Float) : fixe from/to + granularité.
  const handlePreset = (key) => {
    const p = PERIOD_PRESETS.find((x) => x.key === key);
    if (!p) return;
    const w = p.compute(new Date());
    setPreset(key);
    setPeriod(w.period);
    setWindow({ from: w.from, to: w.to });
    setError(null);
  };

  const handlePeriod = (p) => {
    setPreset('custom');
    setPeriod(p);
    setWindow(defaultWindow(p, new Date(`${window.from}T00:00:00Z`)));
  };

  const handleShift = (dir) => {
    setPreset('custom');
    const cfg = WINDOW_BY_PERIOD[period] ?? WINDOW_BY_PERIOD.week;
    setWindow((w) => {
      const from = addDays(new Date(`${w.from}T00:00:00Z`), dir * cfg.shiftDays);
      const to = addDays(new Date(`${w.to}T00:00:00Z`), dir * cfg.shiftDays);
      return { from: isoOf(from), to: isoOf(to) };
    });
  };

  const handleToday = () => { setPreset('custom'); setWindow(defaultWindow(period)); };

  // Sélection libre de période (Du / Au). Valide from<=to ; clamp ≤365j côté UI
  // (le backend rejette déjà >365j). On préserve l'intention de l'utilisateur :
  // on n'ajuste que la borne qui violerait la contrainte.
  const handleWindowChange = ({ from, to }) => {
    setPreset('custom');
    let f = from;
    let t = to;
    if (f > t) {
      // L'utilisateur a inversé : recale la borne non éditée sur l'autre.
      if (f !== window.from) t = f;
      else f = t;
    }
    const fd = new Date(`${f}T00:00:00Z`);
    const td = new Date(`${t}T00:00:00Z`);
    const span = Math.round((td - fd) / 86400000) + 1;
    if (span > 365) {
      setError('Période trop large : 365 jours maximum.');
      // Clamp : on borne la fin à from + 364j (conserve la date de début choisie).
      t = isoOf(addDays(fd, 364));
    } else {
      setError(null);
    }
    setWindow({ from: f, to: t });
  };

  // Conversion d'affichage en jours (÷8, 3 décimales max) ou en heures
  // (1 décimale). Les pourcentages sont inchangés.
  const isDays = unit === 'days';
  const unitLabel = isDays ? 'j' : 'h';
  const conv = useCallback((h) => convertReportHours(h, isDays), [isDays]);

  const rawSeries = report?.series ?? [];
  const series = useMemo(
    () =>
      rawSeries.map((s) => ({
        ...s,
        capacityHours: conv(s.capacityHours) ?? 0,
        allocatedHours: conv(s.allocatedHours) ?? 0,
      })),
    [rawSeries, conv],
  );
  const totals = report?.totals;
  const rangeLabel = `${fmtBucket(window.from, 'day')} → ${fmtBucket(window.to, 'day')}`;

  const bdRows = breakdown?.rows ?? [];
  const isTimetracking = tab === 'timetracking';

  // Sprint 24 — édition d'une saisie depuis Time tracking (lignes canEdit).
  const [editRow, setEditRow] = useState(null);
  const anyEditable = isTimetracking && bdRows.some((r) => r.canEdit);

  // Unité respectée, valeur exacte en jours (ticket support #29).
  const handleExport = () => {
    exportRowsToXlsx(
      bdRows,
      timetrackingExportColumns(unit),
      `time-tracking_${window.from}_${window.to}`,
    );
  };

  // Export CSV WYSIWYG de l'onglet courant (toutes dimensions), unité respectée.
  const handleExportCsv = () => {
    const tabLabel = BREAKDOWN_TABS.find((t) => t.key === tab)?.label ?? tab;
    let columns;
    if (isTimetracking) {
      // Mêmes valeurs exactes que l'export Excel (pas l'arrondi écran de `conv`).
      columns = timetrackingExportColumns(unit);
    } else {
      const u = `(${unitLabel})`;
      columns = [
        { key: 'label', label: tabLabel },
        { key: 'capacityHours', label: `Capacité ${u}`, format: (v) => conv(v) },
        { key: 'loggedHours', label: `Saisi ${u}`, format: (v) => conv(v) },
        { key: 'billableHours', label: `Facturable ${u}`, format: (v) => conv(v) },
        { key: 'nonBillableHours', label: `Non fact. ${u}`, format: (v) => conv(v) },
        { key: 'unloggedHours', label: `Non imputé ${u}`, format: (v) => conv(v) },
        { key: 'timeoffHours', label: `Congés ${u}`, format: (v) => conv(v) },
        { key: 'overtimeHours', label: `H. sup. ${u}`, format: (v) => conv(v) },
        { key: 'loggedPct', label: 'Saisi %', format: (v) => (v == null ? '' : `${v}%`) },
        { key: 'billablePct', label: 'Fact. %', format: (v) => (v == null ? '' : `${v}%`) },
      ];
    }
    exportRowsToCsv(bdRows, columns, `rapport-${tab}_${window.from}_${window.to}`);
  };

  const fmtH = (v) => (v == null ? '—' : `${conv(v)}`);
  const fmtPct = (v) => (v == null ? '—' : `${v}%`);

  return (
    <div style={{ padding: '0 24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '20px 0 4px' }}>
        <BarChart3 size={22} color="var(--accent)" />
        <h1 style={{ margin: 0, fontSize: 20, color: 'var(--text)' }}>Rapports &amp; Analytics</h1>
      </div>

      <ReportControls
        viewMode={viewMode}
        onViewMode={setViewMode}
        period={period}
        onPeriod={handlePeriod}
        scope={scope}
        availableScopes={availableScopes}
        onScope={setScope}
        teamDepth={teamDepth}
        onTeamDepth={setTeamDepth}
        onShift={handleShift}
        onToday={handleToday}
        rangeLabel={rangeLabel}
        window={window}
        onWindowChange={handleWindowChange}
        unit={unit}
        onUnit={setUnit}
        ignoreWeekends={ignoreWeekends}
        onIgnoreWeekends={setIgnoreWeekends}
        presets={PERIOD_PRESETS}
        preset={preset}
        onPreset={handlePreset}
      />

      {error && (
        <div style={{ color: 'var(--danger)', fontSize: 13, padding: '16px 0' }}>{error}</div>
      )}

      {/* Bandeau de cartes KPI (totals utilisation) */}
      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 10,
        }}
      >
        {KPI_CARDS.map((c) => (
          <div
            key={c.key}
            style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-card)',
              padding: '12px 14px',
            }}
          >
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>{c.label}</div>
            <div style={{ fontSize: 22, color: c.color, fontWeight: 700, marginTop: 4 }}>
              {loading || !totals ? '…' : `${conv(totals[c.key] ?? 0)}`}
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}> {unitLabel}</span>
            </div>
            {!loading && totals && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, marginTop: 2 }}>
                {isDays
                  ? `${totals[c.key] ?? 0} h`
                  : `${convertReportHours(totals[c.key] ?? 0, true)} j`}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <DashboardWidget
          title={viewMode === 'scheduled' ? 'Capacité vs demande planifiée' : 'Capacité vs heures saisies'}
          metricKey="deliveryCapacity"
          loading={loading}
          footer={
            totals
              ? `Capacité ${conv(totals.capacityHours)}${unitLabel} · ${viewMode === 'scheduled' ? 'Demande' : 'Saisi'} ${conv(totals.allocatedHours)}${unitLabel} · Utilisation ${totals.utilizationPct ?? '—'}% · Facturable ${totals.billableUtilizationPct ?? '—'}% · ${report?.meta?.personCount ?? 0} pers. / ${report?.meta?.projectsCount ?? 0} projet(s)`
              : null
          }
        >
          <LineAreaChart data={series} height={340} formatLabel={(p) => fmtBucket(p, period)} unitSuffix={unitLabel} />
          {report?.holidays?.length > 0 && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px dashed var(--glass-border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <PartyPopper size={14} color="var(--warning)" />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                  Jours fériés sur la période ({report.holidays.length}) — expliquent les creux
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(() => {
                  // Groupe par DATE : un même jour férié dans 2 pays (Ascension/
                  // Auffahrt) ne fait plus 2 chips, mais 1 chip listant les origines.
                  const byDate = new Map();
                  for (const h of report.holidays) {
                    if (!byDate.has(h.date)) byDate.set(h.date, []);
                    byDate.get(h.date).push(h);
                  }
                  return Array.from(byDate.entries()).map(([date, items]) => (
                    <span
                      key={date}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 6,
                        padding: '3px 10px',
                        borderRadius: 'var(--radius-pill)',
                        border: '1px solid var(--glass-border)',
                        background: 'var(--glass)',
                        fontSize: 12,
                        color: 'var(--text)',
                      }}
                    >
                      <strong style={{ fontWeight: 600 }}>{fmtBucket(date, 'day')}</strong>
                      {items.map((h, i) => (
                        <span
                          key={`${h.countryCode}-${h.region ?? ''}-${h.name}`}
                          title={`${h.name} — ${h.country}${h.region ? ` / ${h.region}` : ''} (${h.scope === 'regional' ? 'régional' : 'national'})`}
                          style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}
                        >
                          {i > 0 && <span style={{ color: 'var(--text-muted)' }}>·</span>}
                          <span>{h.name}</span>
                          <span
                            style={{
                              color: 'var(--accent)',
                              fontSize: 11,
                              fontWeight: 600,
                              background: 'var(--card-bg)',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-sm)',
                              padding: '0 5px',
                            }}
                          >
                            {h.countryCode}{h.region ? ` - ${h.region}` : ''}
                          </span>
                        </span>
                      ))}
                    </span>
                  ));
                })()}
              </div>
            </div>
          )}
        </DashboardWidget>
      </div>

      {/* Onglets de détail (Float-like) */}
      <div
        style={{
          marginTop: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {BREAKDOWN_TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-pill)',
                  border: `1px solid ${active ? 'var(--accent)' : 'var(--glass-border)'}`,
                  background: active ? 'var(--accent)' : 'var(--glass)',
                  color: active ? '#fff' : 'var(--text)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <t.Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>
        {(() => {
          const exportBtn = (label, onClick) => (
            <button
              type="button"
              onClick={onClick}
              disabled={bdLoading || bdRows.length === 0}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--glass-border)',
                background: 'var(--glass)',
                color: 'var(--text)',
                fontSize: 12,
                fontWeight: 600,
                cursor: bdRows.length === 0 ? 'not-allowed' : 'pointer',
                opacity: bdRows.length === 0 ? 0.5 : 1,
              }}
            >
              <Download size={14} />
              {label}
            </button>
          );
          return (
            <div style={{ display: 'inline-flex', gap: 8 }}>
              {exportBtn('Exporter CSV', handleExportCsv)}
              {isTimetracking && exportBtn('Exporter Excel', handleExport)}
            </div>
          );
        })()}
      </div>

      {bdError && (
        <div style={{ color: 'var(--danger)', fontSize: 13, padding: '12px 0' }}>{bdError}</div>
      )}

      <div
        style={{
          marginTop: 12,
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-card)',
          overflow: 'auto',
        }}
      >
        {isTimetracking ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr>
                <th style={th}>Personne</th>
                <th style={th}>Projet</th>
                <th style={th}>Tâche</th>
                <th style={th}>Phase</th>
                <th style={th}>Date</th>
                <th style={{ ...th, textAlign: 'right' }}>{isDays ? 'Jours' : 'Heures'}</th>
                <th style={th}>Facturable</th>
                <th style={th}>Notes</th>
                {anyEditable && <th style={{ ...th, textAlign: 'center' }} />}
              </tr>
            </thead>
            <tbody>
              {bdLoading ? (
                <tr><td style={{ ...td, color: 'var(--text-muted)' }} colSpan={anyEditable ? 9 : 8}>Chargement…</td></tr>
              ) : bdRows.length === 0 ? (
                <tr><td style={{ ...td, color: 'var(--text-muted)' }} colSpan={anyEditable ? 9 : 8}>Aucune saisie sur la période / le périmètre.</td></tr>
              ) : (
                bdRows.map((r, i) => (
                  <tr key={r.id ?? `${r.personId}-${r.date}-${i}`}>
                    <td style={td}>{r.personName}</td>
                    <td style={td}>{r.affaireName}</td>
                    <td style={td}>{r.taskName}</td>
                    <td style={td}>{r.phaseName}</td>
                    <td style={td}>{r.date}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{conv(r.hours)}</td>
                    <td style={td}>{r.billable ? 'Oui' : 'Non'}</td>
                    <td style={{ ...td, color: 'var(--text-muted)' }}>{r.notes}</td>
                    {anyEditable && (
                      <td style={{ ...td, textAlign: 'center' }}>
                        {r.canEdit && (
                          <button
                            type="button"
                            onClick={() => setEditRow(r)}
                            title="Modifier la saisie"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--accent)',
                              display: 'inline-flex',
                            }}
                          >
                            <Pencil size={15} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr>
                <th style={th}>{BREAKDOWN_TABS.find((t) => t.key === tab)?.label}</th>
                <th style={{ ...th, textAlign: 'right' }}>Capacité ({unitLabel})</th>
                <th style={{ ...th, textAlign: 'right' }}>Saisi ({unitLabel})</th>
                <th style={{ ...th, textAlign: 'right' }}>Facturable ({unitLabel})</th>
                <th style={{ ...th, textAlign: 'right' }}>Non fact. ({unitLabel})</th>
                <th style={{ ...th, textAlign: 'right' }}>Non imputé ({unitLabel})</th>
                <th style={{ ...th, textAlign: 'right' }}>Congés ({unitLabel})</th>
                <th style={{ ...th, textAlign: 'right' }}>H. sup. ({unitLabel})</th>
                <th style={{ ...th, textAlign: 'right' }}>Saisi %</th>
                <th style={{ ...th, textAlign: 'right' }}>Fact. %</th>
              </tr>
            </thead>
            <tbody>
              {bdLoading ? (
                <tr><td style={{ ...td, color: 'var(--text-muted)' }} colSpan={10}>Chargement…</td></tr>
              ) : bdRows.length === 0 ? (
                <tr><td style={{ ...td, color: 'var(--text-muted)' }} colSpan={10}>Aucune donnée sur la période / le périmètre.</td></tr>
              ) : (
                bdRows.map((r) => (
                  <tr key={r.key}>
                    <td style={td}>{r.label}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtH(r.capacityHours)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtH(r.loggedHours)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtH(r.billableHours)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtH(r.nonBillableHours)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtH(r.unloggedHours)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtH(r.timeoffHours)}</td>
                    <td style={{ ...td, textAlign: 'right', color: r.overtimeHours ? 'var(--danger)' : 'var(--text)' }}>{fmtH(r.overtimeHours)}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--text-muted)' }}>{fmtPct(r.loggedPct)}</td>
                    <td style={{ ...td, textAlign: 'right', color: 'var(--text-muted)' }}>{fmtPct(r.billablePct)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {editRow && (
        <LoggedTimeEditModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => {
            setEditRow(null);
            loadBreakdown();
          }}
        />
      )}
    </div>
  );
}
